/* MetacogGovernance — SELF-STATE × ENVIRONMENTS × RECONCILIATION (Ultra-Admin only).
 * SELF : état réel déterministe versionné de NOAM (jamais la mémoire historique).
 * ENVIRONMENTS : agrégats par environnement — jamais de contenu inter-tenant.
 * RECONCILIATION : backlog historique confronté au self-state, arbitrage humain. */
import { useEffect, useState } from "react";
import {
  Loader2, RefreshCw, ScanSearch, AlertTriangle, Layers, Globe2,
  GitMerge, ShieldCheck, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";

const RECON_COLORS = {
  IMPLEMENTED: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  PARTIALLY_IMPLEMENTED: "text-teal-300 border-teal-500/40 bg-teal-500/10",
  STILL_RELEVANT: "text-[#EF9F27] border-[#EF9F27]/40 bg-[#EF9F27]/10",
  SUPERSEDED: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  SEMANTIC_DUPLICATE: "text-zinc-400 border-white/15 bg-white/5",
  REJECTED: "text-red-400 border-red-500/40 bg-red-500/10",
  OBSOLETE: "text-zinc-500 border-white/10 bg-white/5",
  UNDETERMINED: "text-purple-300 border-purple-500/40 bg-purple-500/10",
  PROPOSED: "text-zinc-300 border-white/15 bg-white/5",
};

function StatChip({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-lg font-semibold" style={{ color: accent || "#fff" }}>{value ?? "—"}</div>
      <div className="text-[10px] text-zinc-400">{label}</div>
    </div>
  );
}

export function SelfStateTab() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("");
  const load = async () => {
    try {
      const r = await api.get("/workspace/metacognition/self-state/latest");
      setState(r.data);
    } catch { setState(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/workspace/metacognition/self-state/scan");
      notify.success(r.data.changed
        ? `Self-state v${r.data.version} créé (état modifié).`
        : `État inchangé — v${r.data.version} re-vérifiée.`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
    setScanning(false);
  };
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  const caps = (state?.capabilities || []).filter((c) =>
    !filter || c.capability.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="space-y-4 mt-4" data-testid="selfstate-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={scan} disabled={scanning} data-testid="selfstate-scan-btn"
          className="btn-primary text-xs flex items-center gap-1.5">
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
          Scanner l'état réel (déterministe)
        </button>
        {state && (
          <div className="text-[11px] text-zinc-400" data-testid="selfstate-version-info">
            v{state.version} · {String(state.created_at || "").slice(0, 16)} · hash {String(state.hash || "").slice(0, 12)}…
            · re-vérifié {String(state.last_verified_at || "").slice(0, 16)}
          </div>
        )}
      </div>
      {!state ? (
        <div className="text-sm text-zinc-500 py-8">Aucun self-state. Lancez le premier scan.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="selfstate-counts">
            <StatChip label="Capabilities détectées" value={state.counts?.capabilities} accent="#8B7BE8" />
            <StatChip label="Modules fonctionnels" value={state.counts?.functional_modules} />
            <StatChip label="Moteurs plateforme" value={state.counts?.engines} />
            <StatChip label="Démonstrations cognitives" value={state.counts?.cognitive_demonstrations} />
            <StatChip label="Routes API" value={state.counts?.api_routes} />
            <StatChip label="Preservation Set" value={state.preservation_set?.value} accent="#1D9E75" />
          </div>
          <div className="text-[10px] text-zinc-500">{state.doctrine}</div>
          {(state.inconsistencies || []).length > 0 && (
            <div className="noam-card p-3 border-amber-500/30" data-testid="selfstate-inconsistencies">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> {state.inconsistencies.length} incohérence(s) détectée(s)
              </div>
              {state.inconsistencies.slice(0, 12).map((i, n) => (
                <div key={n} className="text-[11px] text-zinc-300 py-0.5">· <span className="text-zinc-500">[{i.type}]</span> {i.detail}</div>
              ))}
            </div>
          )}
          {(state.undetermined || []).length > 0 && (
            <div className="noam-card p-3" data-testid="selfstate-undetermined">
              <div className="text-xs font-semibold text-purple-300 mb-1">Non déterminable automatiquement</div>
              {state.undetermined.map((u, n) => <div key={n} className="text-[11px] text-zinc-400 py-0.5">· {u}</div>)}
            </div>
          )}
          <div className="noam-card p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-white flex-1">Capabilities ({caps.length})</div>
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer…"
                data-testid="selfstate-filter"
                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-white outline-none w-44" />
            </div>
            <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5" data-testid="selfstate-capabilities-list">
              {caps.map((c, n) => (
                <div key={n} className="py-1.5 flex items-start gap-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${
                    c.kind === "engine" ? "text-sky-300 border-sky-500/40" :
                    c.kind === "cognitive_demonstration" ? "text-[#8B7BE8] border-[#8B7BE8]/40" :
                    "text-zinc-300 border-white/15"}`}>{c.kind === "functional_module" ? "module" : c.kind === "engine" ? "moteur" : "cognitif"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white truncate">{c.capability}</div>
                    <div className="text-[10px] text-zinc-500">
                      {c.status} · {c.validation_level} · vérifié {String(c.last_verified || "").slice(0, 10)}
                      {(c.evidence || [])[0]?.source ? ` · ${String(c.evidence[0].source).replace("/app/", "")}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function EnvironmentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/workspace/metacognition/environments")
      .then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-sm text-zinc-500 py-8">Indisponible.</div>;
  const envs = [...(data.environments || [])].sort((a, b) => b.modules_with_data - a.modules_with_data);
  return (
    <div className="space-y-4 mt-4" data-testid="environments-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatChip label="Environnements" value={envs.length} accent="#378ADD" />
        <StatChip label="Actifs" value={data.patterns?.active_environments} accent="#1D9E75" />
        <StatChip label="Vides" value={data.patterns?.empty_environments} />
        <StatChip label="Doctrine" value="Agrégats only" accent="#8B7BE8" />
      </div>
      <div className="text-[10px] text-zinc-500">{data.doctrine}</div>
      <div className="noam-card p-3">
        <div className="text-xs font-semibold text-white mb-2">Adoption des modules (part des environnements avec données)</div>
        <div className="flex flex-wrap gap-1.5" data-testid="env-adoption-patterns">
          {Object.entries(data.patterns?.module_data_adoption_pct || {}).slice(0, 20).map(([k, v]) => (
            <span key={k} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-zinc-300">
              {k} <span className="text-[#EF9F27]">{v}%</span>
            </span>
          ))}
        </div>
      </div>
      <div className="noam-card p-3 overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="env-table">
          <thead><tr className="text-zinc-500 text-left">
            <th className="py-1 font-normal">Environnement</th><th className="font-normal">Plan</th>
            <th className="font-normal text-center">Users</th><th className="font-normal text-center">Modules dispo</th>
            <th className="font-normal text-center">Avec données</th><th className="font-normal text-center">Appels IA</th>
            <th className="font-normal text-center">Signaux ouverts</th><th className="font-normal">Dernière connexion</th>
          </tr></thead>
          <tbody>
            {envs.map((e) => (
              <tr key={e.organisation_id} className="border-t border-white/5 text-zinc-300">
                <td className="py-1.5 text-white truncate max-w-[220px]">{e.name}</td>
                <td>{e.plan}</td>
                <td className="text-center">{e.users}</td>
                <td className="text-center">{e.modules_available}</td>
                <td className="text-center text-[#EF9F27]">{e.modules_with_data}</td>
                <td className="text-center">{e.llm_calls}</td>
                <td className="text-center">{e.signals_open}</td>
                <td className="text-zinc-500">{String(e.last_login_at || "—").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReconciliationTab() {
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const load = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get("/workspace/metacognition/reconciliation/summary"),
        api.get("/workspace/metacognition/reconciliation/status"),
      ]);
      setSummary(s.data); setStatus(st.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.status !== "running") return;
    const t = setInterval(async () => {
      const r = await api.get("/workspace/metacognition/reconciliation/status").catch(() => null);
      if (r) {
        setStatus(r.data);
        if (r.data.status !== "running") { clearInterval(t); load(); }
      }
    }, 5000);
    return () => clearInterval(t);
  }, [status?.status]);
  const start = async () => {
    setStarting(true);
    try {
      await api.post("/workspace/metacognition/reconciliation/start");
      notify.success("Réconciliation lancée en arrière-plan.");
      const r = await api.get("/workspace/metacognition/reconciliation/status");
      setStatus(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setStarting(false);
  };
  const arbitrate = async (id, st) => {
    try {
      await api.post(`/workspace/metacognition/improvements/${id}/reconciliation-arbitrate`, { status: st });
      notify.success(`Arbitré → ${st}`);
      load();
    } catch (e) { notify.error(fmtError(e)); }
  };
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  const dist = summary?.distribution || {};
  return (
    <div className="space-y-4 mt-4" data-testid="reconciliation-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={start} disabled={starting || status?.status === "running"}
          data-testid="reconciliation-start-btn" className="btn-primary text-xs flex items-center gap-1.5">
          {status?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />}
          {status?.status === "running" ? "Réconciliation en cours…" : "Réconcilier le backlog contre le self-state"}
        </button>
        {status && status.status !== "never_run" && (
          <div className="text-[11px] text-zinc-400" data-testid="reconciliation-status">
            {status.status} · {status.progress || ""} {status.self_state_version ? `· self-state v${status.self_state_version}` : ""}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="reconciliation-distribution">
        {Object.entries(dist).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full border ${RECON_COLORS[k] || RECON_COLORS.PROPOSED}`}>
            {k} · {v}
          </span>
        ))}
      </div>
      <div className="noam-card p-3">
        <div className="text-xs font-semibold text-white mb-2">
          Réellement ouvertes ({(summary?.open_items || []).length}) : STILL_RELEVANT · PARTIALLY_IMPLEMENTED · UNDETERMINED
        </div>
        <div className="max-h-[520px] overflow-y-auto divide-y divide-white/5" data-testid="reconciliation-open-list">
          {(summary?.open_items || []).map((it) => (
            <div key={it.id} className="py-2">
              <div className="flex items-start gap-2">
                <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${RECON_COLORS[it.reconciliation?.status] || ""}`}>
                  {it.reconciliation?.status}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-white">{it.title}</div>
                  <div className="text-[10px] text-zinc-500">
                    {it.impact_module} · {it.priority} {it.reconciliation?.origin ? `· ${it.reconciliation.origin}` : ""}
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-0.5">{it.reconciliation?.reason}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => arbitrate(it.id, "IMPLEMENTED")} title="Arbitrer : déjà implémenté"
                    data-testid={`arb-impl-${it.id}`}
                    className="p-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/30"><Check className="w-3 h-3" /></button>
                  <button onClick={() => arbitrate(it.id, "OBSOLETE")} title="Arbitrer : obsolète"
                    data-testid={`arb-obs-${it.id}`}
                    className="p-1 rounded bg-white/5 text-zinc-400 hover:bg-white/15 text-[9px] px-1.5">obsolète</button>
                  <button onClick={() => arbitrate(it.id, "STILL_RELEVANT")} title="Arbitrer : toujours pertinent"
                    data-testid={`arb-rel-${it.id}`}
                    className="p-1 rounded bg-[#EF9F27]/15 text-[#EF9F27] hover:bg-[#EF9F27]/30 text-[9px] px-1.5">pertinent</button>
                </div>
              </div>
            </div>
          ))}
          {!(summary?.open_items || []).length && (
            <div className="text-[11px] text-zinc-500 py-3">Aucune proposition ouverte. Lancez une réconciliation.</div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3" /> Le moteur propose, l'Ultra-Admin arbitre. Historique append-only, accès audités.
      </div>
    </div>
  );
}
